// src/modules/ProductCatalog.jsx
import React, { useState, useMemo } from 'react';
import { C } from '../constants';
import { apiCall } from '../api';

const generateRequestId = () => {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `PR-${timestamp}-${random}`;
};

export default function ProductCatalog({ manufacturer, userInfo, quoteList, setQuoteList, onBack, onSubmitSuccess, onSubmitComplete }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const products = useMemo(
    () => manufacturer.products || [],
    [manufacturer]
  );

  const categories = useMemo(() => {
    const cats = [...new Set(products.map(p => p.category))].sort();
    return ['All', ...cats];
  }, [products]);

  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return products.filter(p => {
      const matchSearch = !term ||
        p.partNumber.toLowerCase().includes(term) ||
        p.description.toLowerCase().includes(term) ||
        p.category.toLowerCase().includes(term);
      const matchCat = categoryFilter === 'All' || p.category === categoryFilter;
      return matchSearch && matchCat;
    });
  }, [products, searchTerm, categoryFilter]);

  const quoteItems = Object.values(quoteList);
  const totalItems = quoteItems.reduce((sum, i) => sum + i.qty, 0);

  const updateQty = (product, newQty) => {
    const qty = Math.max(0, parseInt(newQty) || 0);
    if (qty === 0) {
      setQuoteList(prev => { const next = { ...prev }; delete next[product.partNumber]; return next; });
    } else {
      setQuoteList(prev => ({ ...prev, [product.partNumber]: { ...product, qty } }));
    }
  };

  const removeItem = (partNumber) => {
    setQuoteList(prev => { const next = { ...prev }; delete next[partNumber]; return next; });
  };

  const handleSubmit = async () => {
    if (quoteItems.length === 0) return;
    setSubmitting(true);
    setSubmitError('');
    const requestId = generateRequestId();
    const now = new Date();
    const timestamp = now.toLocaleString('en-US', { timeZone: 'America/New_York' });
    const itemsPayload = quoteItems.map(item => ({
      category: item.category, partNumber: item.partNumber,
      description: item.description, incoterms: item.incoterms, qty: item.qty,
    }));
    try {
      const data = await apiCall('submitProductRequest', {
        requestId, timestamp,
        manufacturer: manufacturer.name,
        solutionType: `Product Request: ${manufacturer.name}`,
        name: userInfo?.name || '', email: userInfo?.email || '', phone: userInfo?.phone || '',
        company: userInfo?.company || '',
        items: JSON.stringify(itemsPayload), itemCount: quoteItems.length, totalQty: totalItems,
      });
      if (data.success) {
        // Reset parent unsaved state FIRST so no abandon warning fires on modal buttons
        onSubmitSuccess && onSubmitSuccess();
        setSubmitted(true);
      } else {
        setSubmitError(data.message || 'Submission failed. Please try again.');
      }
    } catch (err) {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success modal ──
  if (submitted) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ background: C.white, borderRadius: 16, padding: '40px 36px', maxWidth: 460, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 28 }}>✅</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: C.t1, margin: '0 0 10px' }}>Quote Request Submitted!</h2>
          <p style={{ fontSize: 14, color: C.t2, lineHeight: 1.6, margin: '0 0 28px' }}>
            Your product quote request for <strong>{manufacturer.name}</strong> has been received. Our team will review it and get back to you shortly.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={() => { onSubmitComplete && onSubmitComplete(); }}
              style={{ padding: '11px 22px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.t2, fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Back to Manufacturers
            </button>
            <button
              onClick={() => setSubmitted(false)}
              style={{ padding: '11px 22px', borderRadius: 8, border: 'none', background: C.navy, color: C.white, fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', margin: '-32px -28px', overflow: 'hidden' }}>

      {/* ── Left: Product Table ── */}
      <div style={{ flex: 1, padding: '28px 24px', overflowY: 'auto', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <button onClick={onBack} style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.white, color: C.t1, fontWeight: '600', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'inherit' }}>
            ← Back
          </button>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: C.t1 }}>{manufacturer.name}</h2>
            <p style={{ margin: 0, fontSize: '13px', color: C.t2 }}>{manufacturer.description}</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <input type="text" placeholder="Search by part number, description, or category..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '9px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, fontSize: '13px', outline: 'none', background: C.grayBg, fontFamily: 'inherit' }} />
          {categories.length > 2 && (
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
              style={{ padding: '9px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, fontSize: '13px', background: C.grayBg, cursor: 'pointer', color: C.t1, outline: 'none', fontFamily: 'inherit' }}>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>

        {searchTerm.trim() && (
          <div style={{ fontSize: '12px', color: C.t3, marginBottom: '10px' }}>
            {filteredProducts.length} result{filteredProducts.length !== 1 ? 's' : ''} for "{searchTerm.trim()}"
          </div>
        )}

        {products.length === 0 ? <EmptyProductsState manufacturer={manufacturer.name} /> :
          filteredProducts.length === 0 ? <div style={{ textAlign: 'center', padding: '60px 20px', color: C.t3, fontSize: '14px' }}>No products match your search.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: C.grayBg }}>
                  {['Category', 'Part Number', 'Description', 'Delivery Location', 'Quantity'].map(col => (
                    <th key={col} style={{ padding: '10px 12px', textAlign: col === 'Quantity' ? 'center' : 'left', fontWeight: '600', color: C.t2, fontSize: '12px', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap', position: 'sticky', top: 0, background: C.grayBg, zIndex: 1 }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product, idx) => {
                  const currentQty = quoteList[product.partNumber]?.qty || 0;
                  return (
                    <tr key={`${product.partNumber}-${idx}`} style={{ background: currentQty > 0 ? `${C.navy}08` : idx % 2 === 0 ? C.white : C.grayBg, borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '10px 12px', color: C.t2, fontSize: '12px', whiteSpace: 'nowrap' }}>{product.category}</td>
                      <td style={{ padding: '10px 12px', fontWeight: '600', color: C.t1, whiteSpace: 'nowrap' }}>{product.partNumber}</td>
                      <td style={{ padding: '10px 12px', color: C.t1, lineHeight: '1.4' }}>{product.description}</td>
                      <td style={{ padding: '10px 12px', color: C.t2, fontSize: '12px', whiteSpace: 'nowrap' }}>{product.incoterms}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <QtyControl qty={currentQty} onChange={(qty) => updateQty(product, qty)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Right: Quote List Panel ── */}
      <QuoteListPanel items={quoteItems} manufacturer={manufacturer.name} onUpdateQty={updateQty} onRemoveItem={removeItem} onSubmit={handleSubmit} submitting={submitting} submitError={submitError} />
    </div>
  );
}

function QtyControl({ qty, onChange }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <button onClick={() => onChange(qty - 1)} style={{ width: '26px', height: '26px', borderRadius: '6px', border: `1px solid ${C.border}`, background: qty > 0 ? C.white : C.grayBg, color: C.t1, cursor: 'pointer', fontSize: '15px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, fontFamily: 'inherit' }}>−</button>
      <input type="number" min="0" value={qty === 0 ? '' : qty} placeholder="0" onChange={e => onChange(parseInt(e.target.value) || 0)}
        style={{ width: '48px', height: '26px', textAlign: 'center', borderRadius: '6px', border: `1px solid ${C.border}`, fontSize: '13px', fontWeight: '600', color: C.t1, outline: 'none', background: C.white, MozAppearance: 'textfield', fontFamily: 'inherit' }} />
      <button onClick={() => onChange(qty + 1)} style={{ width: '26px', height: '26px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, color: C.navy, cursor: 'pointer', fontSize: '15px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, fontFamily: 'inherit' }}>+</button>
    </div>
  );
}

function QuoteListPanel({ items, manufacturer, onUpdateQty, onRemoveItem, onSubmit, submitting, submitError }) {
  const totalQty = items.reduce((s, i) => s + i.qty, 0);
  return (
    <div style={{ width: '300px', minWidth: '280px', flexShrink: 0, borderLeft: `1px solid ${C.border}`, background: C.grayBg, height: '100%', display: 'flex', flexDirection: 'column', padding: '24px 16px', boxSizing: 'border-box' }}>
      <div style={{ marginBottom: '16px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: C.t1 }}>Quote List</h3>
          {items.length > 0 && (
            <span style={{ background: C.navy, color: C.white, borderRadius: '12px', padding: '2px 9px', fontSize: '12px', fontWeight: '700' }}>
              {totalQty} {totalQty === 1 ? 'item' : 'items'}
            </span>
          )}
        </div>
        <p style={{ margin: '4px 0 0', fontSize: '12px', color: C.t3 }}>{manufacturer}</p>
      </div>
      <div style={{ borderTop: `1px solid ${C.border}`, marginBottom: '14px', flexShrink: 0 }} />
      {items.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: C.t3, textAlign: 'center', gap: '8px', padding: '20px' }}>
          <div style={{ fontSize: '32px' }}>📋</div>
          <div style={{ fontSize: '13px', lineHeight: '1.5' }}>Your Quote List is empty.<br />Set quantities in the table to add products.</div>
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', minHeight: 0 }}>
          {items.map(item => (
            <div key={item.partNumber} style={{ background: C.white, borderRadius: '8px', padding: '10px 12px', border: `1px solid ${C.border}`, fontSize: '12px', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                <div style={{ fontWeight: '700', color: C.t1, fontSize: '13px' }}>{item.partNumber}</div>
                <button onClick={() => onRemoveItem(item.partNumber)} title="Remove item"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.t3, padding: '0 0 0 6px', lineHeight: 1, fontSize: '14px', flexShrink: 0 }}
                  onMouseEnter={e => e.currentTarget.style.color = C.red}
                  onMouseLeave={e => e.currentTarget.style.color = C.t3}>🗑</button>
              </div>
              <div style={{ color: C.t2, lineHeight: '1.3', marginBottom: '6px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {item.description}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ color: C.t3, fontSize: '11px' }}>{item.incoterms}</div>
                <QtyControl qty={item.qty} onChange={(qty) => onUpdateQty(item, qty)} />
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: '16px', borderTop: `1px solid ${C.border}`, paddingTop: '14px', flexShrink: 0 }}>
        {submitError && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: C.red, marginBottom: '12px' }}>
            {submitError}
          </div>
        )}
        <button onClick={onSubmit} disabled={items.length === 0 || submitting}
          style={{ width: '100%', padding: '12px', borderRadius: '8px', border: 'none', background: items.length === 0 ? C.border : C.navy, color: items.length === 0 ? C.t3 : C.white, fontWeight: '700', fontSize: '14px', cursor: items.length === 0 ? 'not-allowed' : 'pointer', transition: 'background 0.2s', fontFamily: 'inherit' }}>
          {submitting ? 'Submitting...' : 'Submit Quote Request'}
        </button>
        <p style={{ margin: '8px 0 0', fontSize: '11px', color: C.t3, textAlign: 'center' }}>Our team will contact you with pricing.</p>
      </div>
    </div>
  );
}

function EmptyProductsState({ manufacturer }) {
  return (
    <div style={{ textAlign: 'center', padding: '80px 20px', color: C.t3 }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>🗂️</div>
      <div style={{ fontSize: '16px', fontWeight: '600', color: C.t2, marginBottom: '8px' }}>Product catalog coming soon</div>
      <div style={{ fontSize: '13px', lineHeight: '1.6' }}>The {manufacturer} product list is being set up.<br />Check back soon or contact us directly.</div>
    </div>
  );
}
